// apps/admin/src/pages/settings/MerchantConfigsTab.tsx
//
// Phase 8.3 Wave 6 Task 41 — Merchant Configs tab.
//
// vg-binding: CONTEXT:decisions=P8.D-100,P8.D-101,P8.D-120,P8.D-170,P8.D-219,P8.D-466
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Route: /system/settings/merchant-configs
//
// Scope (per task-41 PLAN):
//   - default_credit_rules (free JSON record — surfaced as a JSON textarea
//     for now; richer editor lands with task-42 sibling)
//   - auto_cancel_draft_minutes (int >= 0)
//   - registration_survey_id (string|null — survey CRUD lives in task-44
//     email templates; this tab just stores the active reference)
//   - tier_evaluation_window_days (30 / 60 / 90 / lifetime)
//   - tier_rules (array of { tier, criteria } — render as a JSON textarea
//     for the first cut; visual builder lands in a sibling task)
//   - at_risk_thresholds (surfaced under "Merchant At-Risk
//     thresholds" with numeric fields for each leaf)
//
// Permission gates:
//   - Read: `system:settings:read`
//   - Write: `system:settings:write:merchant`

import { useCallback, useMemo, useState, type JSX } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormLabel,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  useAuth,
  usePermission,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import { ConflictDiffModal } from '../../features/settings/ConflictDiffModal.js';
import { StickySaveBar } from '../../features/settings/StickySaveBar.js';
import { useSettings } from '../../features/settings/useSettings.js';
import type { MerchantSettingsValues } from '../../api/systemSettings.api.js';

const TIER_WINDOW_OPTIONS = ['30', '60', '90', 'lifetime'] as const;

// Stable empty-roles ref — a `[]` literal inside the useAuth selector returns
// a new array each render when roles is nullish → useSyncExternalStore
// infinite loop (dbg-20260710 #2593). Coalesce against this frozen constant.
const EMPTY_ROLES: readonly unknown[] = [];

function stringifyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function MerchantConfigsTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const canRead = usePermission('system:settings:read');
  const canWrite = usePermission('system:settings:write:merchant');
  const userRoles = useAuth((s) => s.user?.roles) ?? EMPTY_ROLES;
  const isSuperAdmin = useMemo(
    () =>
      userRoles.some((r) =>
        typeof r === 'string'
          ? r === 'superadmin'
          : (r as { role?: string })?.role === 'superadmin',
      ),
    [userRoles],
  );

  const {
    loading,
    saving,
    envelope,
    draft,
    dirty,
    conflict,
    setDraft,
    reload,
    save,
    forceOverwrite,
    dismissConflict,
  } = useSettings<MerchantSettingsValues>('merchant');

  // Local mirrors for the two JSON-textarea fields. We keep raw strings
  // alongside the typed draft so the user can mid-type invalid JSON without
  // wiping their work. On blur (or save) we parse + commit; on parse error
  // we surface an inline message.
  const [creditRulesText, setCreditRulesText] = useState<string | null>(null);
  const [tierRulesText, setTierRulesText] = useState<string | null>(null);
  const [creditRulesError, setCreditRulesError] = useState<string | null>(null);
  const [tierRulesError, setTierRulesError] = useState<string | null>(null);

  const commitCreditRules = useCallback((): void => {
    if (creditRulesText == null) return;
    try {
      const parsed = JSON.parse(creditRulesText) as Record<string, unknown>;
      setDraft((prev) => ({ ...prev, default_credit_rules: parsed }));
      setCreditRulesError(null);
    } catch (err) {
      setCreditRulesError(
        err instanceof Error ? err.message : t('merchantConfigs.invalidJsonFallback'),
      );
    }
  }, [creditRulesText, setDraft, t]);

  const commitTierRules = useCallback((): void => {
    if (tierRulesText == null) return;
    try {
      const parsed = JSON.parse(tierRulesText) as MerchantSettingsValues['tier_rules'];
      setDraft((prev) => ({ ...prev, tier_rules: parsed }));
      setTierRulesError(null);
    } catch (err) {
      setTierRulesError(err instanceof Error ? err.message : t('merchantConfigs.invalidJsonFallback'));
    }
  }, [tierRulesText, setDraft, t]);

  const handleSave = useCallback((): void => {
    // Flush any pending JSON edits before PATCH.
    commitCreditRules();
    commitTierRules();
    if (creditRulesError || tierRulesError) return;
    void save();
  }, [commitCreditRules, commitTierRules, creditRulesError, tierRulesError, save]);

  if (!canRead) {
    return (
      <div className="rounded-lg border border-pw-border bg-pw-surface p-6 text-sm text-pw-text-muted">
        {t('merchantConfigs.noPermission')}
      </div>
    );
  }

  if (loading || !envelope || !draft) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner label={t('merchantConfigs.loading')} />
      </div>
    );
  }

  const atRisk = draft.at_risk_thresholds ?? {
    low_rating_below: 0,
    high_rejection_above: 0,
    complaint_spike_count: 0,
    complaint_window_hours: 0,
  };

  const updateAtRisk = (
    key: keyof NonNullable<MerchantSettingsValues['at_risk_thresholds']>,
    value: number,
  ): void => {
    setDraft((prev) => ({
      ...prev,
      at_risk_thresholds: { ...atRisk, [key]: value },
    }));
  };

  return (
    <div className="flex flex-col gap-6" data-testid="settings-merchant-tab">
      <div className="flex items-center justify-between text-xs text-pw-text-muted">
        <span>
          {t('merchantConfigs.versionLabel')}{' '}
          <strong className="font-semibold">v{envelope.version}</strong>
          {envelope.updated_by_name ? (
            <> · {t('merchantConfigs.lastEditedBy', { name: envelope.updated_by_name })}</>
          ) : null}
          {envelope.updated_at ? (
            <> · {new Date(envelope.updated_at).toLocaleString()}</>
          ) : null}
        </span>
        <button
          type="button"
          onClick={() => {
            void reload();
          }}
          className="text-xs font-medium text-pw-primary-strong hover:underline"
          data-testid="settings-merchant-reload-btn"
        >
          {t('merchantConfigs.reload')}
        </button>
      </div>

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave();
        }}
      >
        <Card>
          <CardHeader>
            <CardTitle>{t('merchantConfigs.draftSignup.title')}</CardTitle>
            <CardDescription>
              {t('merchantConfigs.draftSignup.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FormField>
              <FormLabel htmlFor="merchant-auto-cancel">
                {t('merchantConfigs.autoCancel.label')}
              </FormLabel>
              <FormControl>
                <Input
                  id="merchant-auto-cancel"
                  type="number"
                  min={0}
                  value={draft.auto_cancel_draft_minutes}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      auto_cancel_draft_minutes:
                        Number.parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  disabled={!canWrite}
                  data-testid="settings-merchant-auto-cancel"
                />
              </FormControl>
              <p className="text-xs text-pw-text-muted">
                {t('merchantConfigs.autoCancel.hint')}
              </p>
            </FormField>

            <FormField>
              <FormLabel htmlFor="merchant-registration-survey">
                {t('merchantConfigs.survey.label')}
              </FormLabel>
              <FormControl>
                <Input
                  id="merchant-registration-survey"
                  value={draft.registration_survey_id ?? ''}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      registration_survey_id:
                        e.target.value === '' ? null : e.target.value,
                    }))
                  }
                  placeholder={t('merchantConfigs.survey.placeholder')}
                  disabled={!canWrite}
                  data-testid="settings-merchant-survey-id"
                />
              </FormControl>
            </FormField>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('merchantConfigs.tier.title')}</CardTitle>
            <CardDescription>
              {t('merchantConfigs.tier.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <FormField>
              <FormLabel htmlFor="merchant-tier-window">
                {t('merchantConfigs.tierWindow.label')}
              </FormLabel>
              <FormControl>
                <Select
                  value={draft.tier_evaluation_window_days}
                  onValueChange={(value) =>
                    setDraft((prev) => ({
                      ...prev,
                      tier_evaluation_window_days:
                        value as MerchantSettingsValues['tier_evaluation_window_days'],
                    }))
                  }
                  disabled={!canWrite}
                >
                  <SelectTrigger
                    id="merchant-tier-window"
                    data-testid="settings-merchant-tier-window"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIER_WINDOW_OPTIONS.map((opt) => (
                      <SelectItem key={opt} value={opt}>
                        {opt === 'lifetime'
                          ? t('merchantConfigs.tierWindow.lifetime')
                          : t('merchantConfigs.tierWindow.lastDays', { days: opt })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="merchant-tier-rules">
                {t('merchantConfigs.tierRules.label')}
              </FormLabel>
              <FormControl>
                <textarea
                  id="merchant-tier-rules"
                  value={tierRulesText ?? stringifyJson(draft.tier_rules)}
                  onChange={(e) => {
                    setTierRulesText(e.target.value);
                    setTierRulesError(null);
                  }}
                  onBlur={commitTierRules}
                  rows={8}
                  spellCheck={false}
                  disabled={!canWrite}
                  className="block w-full rounded-md border border-pw-border bg-pw-bg p-3 font-mono text-xs text-pw-text focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="settings-merchant-tier-rules"
                />
              </FormControl>
              {tierRulesError ? (
                <p className="text-xs font-medium text-red-600 dark:text-red-300">
                  {t('merchantConfigs.tierRules.parseError', { message: tierRulesError })}
                </p>
              ) : (
                <p className="text-xs text-pw-text-muted">
                  {t('merchantConfigs.tierRules.hint')}
                </p>
              )}
            </FormField>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('merchantConfigs.creditRules.title')}</CardTitle>
            <CardDescription>
              {t('merchantConfigs.creditRules.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <FormField>
              <FormLabel htmlFor="merchant-credit-rules">
                default_credit_rules (JSON)
              </FormLabel>
              <FormControl>
                <textarea
                  id="merchant-credit-rules"
                  value={creditRulesText ?? stringifyJson(draft.default_credit_rules)}
                  onChange={(e) => {
                    setCreditRulesText(e.target.value);
                    setCreditRulesError(null);
                  }}
                  onBlur={commitCreditRules}
                  rows={8}
                  spellCheck={false}
                  disabled={!canWrite}
                  className="block w-full rounded-md border border-pw-border bg-pw-bg p-3 font-mono text-xs text-pw-text focus:border-pw-primary focus:outline-none focus:ring-2 focus:ring-pw-primary/20 disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="settings-merchant-credit-rules"
                />
              </FormControl>
              {creditRulesError ? (
                <p className="text-xs font-medium text-red-600 dark:text-red-300">
                  {t('merchantConfigs.creditRules.parseError', { message: creditRulesError })}
                </p>
              ) : null}
            </FormField>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>{t('merchantConfigs.atRisk.title')}</CardTitle>
            <CardDescription>
              {t('merchantConfigs.atRisk.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <FormField>
              <FormLabel htmlFor="merchant-atrisk-low-rating">
                {t('merchantConfigs.atRisk.lowRating')}
              </FormLabel>
              <FormControl>
                <Input
                  id="merchant-atrisk-low-rating"
                  type="number"
                  step="0.1"
                  min={0}
                  max={5}
                  value={atRisk.low_rating_below}
                  onChange={(e) =>
                    updateAtRisk(
                      'low_rating_below',
                      Number.parseFloat(e.target.value) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-merchant-atrisk-rating"
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="merchant-atrisk-rejection">
                {t('merchantConfigs.atRisk.rejection')}
              </FormLabel>
              <FormControl>
                <Input
                  id="merchant-atrisk-rejection"
                  type="number"
                  step="0.1"
                  min={0}
                  max={100}
                  value={atRisk.high_rejection_above}
                  onChange={(e) =>
                    updateAtRisk(
                      'high_rejection_above',
                      Number.parseFloat(e.target.value) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-merchant-atrisk-rejection"
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="merchant-atrisk-complaint-count">
                {t('merchantConfigs.atRisk.complaintCount')}
              </FormLabel>
              <FormControl>
                <Input
                  id="merchant-atrisk-complaint-count"
                  type="number"
                  min={0}
                  value={atRisk.complaint_spike_count}
                  onChange={(e) =>
                    updateAtRisk(
                      'complaint_spike_count',
                      Number.parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-merchant-atrisk-complaint-count"
                />
              </FormControl>
            </FormField>

            <FormField>
              <FormLabel htmlFor="merchant-atrisk-complaint-window">
                {t('merchantConfigs.atRisk.complaintWindow')}
              </FormLabel>
              <FormControl>
                <Input
                  id="merchant-atrisk-complaint-window"
                  type="number"
                  min={1}
                  value={atRisk.complaint_window_hours}
                  onChange={(e) =>
                    updateAtRisk(
                      'complaint_window_hours',
                      Number.parseInt(e.target.value, 10) || 0,
                    )
                  }
                  disabled={!canWrite}
                  data-testid="settings-merchant-atrisk-window"
                />
              </FormControl>
            </FormField>
          </CardContent>
        </Card>

        <StickySaveBar
          dirty={dirty || !!creditRulesText || !!tierRulesText}
          saving={saving}
          canWrite={canWrite}
          onSave={handleSave}
          onDiscard={() => {
            setCreditRulesText(null);
            setTierRulesText(null);
            setCreditRulesError(null);
            setTierRulesError(null);
            void reload();
          }}
        />
      </Form>

      {conflict ? (
        <ConflictDiffModal
          open={conflict.open}
          yourValues={conflict.yourValues as unknown as Record<string, unknown>}
          serverValues={conflict.serverValues as unknown as Record<string, unknown>}
          currentVersion={conflict.currentVersion}
          expectedVersion={conflict.expectedVersion}
          isSuperAdmin={isSuperAdmin}
          onReload={() => {
            dismissConflict();
            void reload();
          }}
          onForceOverwrite={() => {
            void forceOverwrite();
          }}
          onClose={dismissConflict}
        />
      ) : null}
    </div>
  );
}

export default MerchantConfigsTab;
